/**
 * PricingStep Component
 *
 * Step 5 of listing wizard: pricing configuration.
 */

import React from 'react';
import { ListingFormData, ListingPricing } from '../../hooks';

interface PricingStepProps {
  data: ListingFormData;
  onPricingChange: (updates: Partial<ListingPricing>) => void;
}

export const PricingStep: React.FC<PricingStepProps> = ({
  data,
  onPricingChange,
}) => {
  const { pricing } = data;

  // Calculate estimated earnings
  const estimatedMonthly = React.useMemo(() => {
    if (!pricing.nightlyRate) return 0;
    const avgNightlyRate = pricing.weekendRate
      ? (pricing.nightlyRate * 5 + pricing.weekendRate * 2) / 7
      : pricing.nightlyRate;
    const occupancyRate = 0.6; // Assume 60% occupancy
    const daysPerMonth = 30;
    const cleaningFees = (pricing.cleaningFee || 0) * (daysPerMonth * occupancyRate / 3);
    return Math.round(avgNightlyRate * daysPerMonth * occupancyRate + cleaningFees);
  }, [pricing]);

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Set your pricing
        </h3>
        <p className="text-gray-600">
          You can change your prices anytime.
        </p>
      </div>

      {/* Nightly Rate */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nightly rate <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
            $
          </span>
          <input
            type="number"
            min="0"
            value={pricing.nightlyRate || ''}
            onChange={(e) =>
              onPricingChange({ nightlyRate: parseFloat(e.target.value) || 0 })
            }
            placeholder="0"
            className="w-full pl-8 pr-16 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
            USD / night
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          This is your base price for weekday nights
        </p>
      </div>

      {/* Weekend Rate */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Weekend rate
          <span className="text-gray-400 font-normal ml-2">(optional)</span>
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
            $
          </span>
          <input
            type="number"
            min="0"
            value={pricing.weekendRate || ''}
            onChange={(e) =>
              onPricingChange({
                weekendRate: e.target.value ? parseFloat(e.target.value) : null,
              })
            }
            placeholder={pricing.nightlyRate?.toString() || '0'}
            className="w-full pl-8 pr-16 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
            USD / night
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Friday and Saturday nights. Leave empty to use nightly rate.
        </p>
      </div>

      {/* Discounts */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Weekly discount
            <span className="text-gray-400 font-normal ml-2">(optional)</span>
          </label>
          <div className="relative">
            <input
              type="number"
              min="0"
              max="90"
              value={pricing.weeklyDiscount || ''}
              onChange={(e) =>
                onPricingChange({
                  weeklyDiscount: e.target.value ? parseFloat(e.target.value) : null,
                })
              }
              placeholder="0"
              className="w-full pl-4 pr-8 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
              %
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">For stays of 7+ nights</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Monthly discount
            <span className="text-gray-400 font-normal ml-2">(optional)</span>
          </label>
          <div className="relative">
            <input
              type="number"
              min="0"
              max="90"
              value={pricing.monthlyDiscount || ''}
              onChange={(e) =>
                onPricingChange({
                  monthlyDiscount: e.target.value ? parseFloat(e.target.value) : null,
                })
              }
              placeholder="0"
              className="w-full pl-4 pr-8 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
              %
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">For stays of 28+ nights</p>
        </div>
      </div>

      {/* Additional Fees */}
      <div>
        <h4 className="font-medium text-gray-900 mb-4">Additional fees</h4>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cleaning fee
              <span className="text-gray-400 font-normal ml-2">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                $
              </span>
              <input
                type="number"
                min="0"
                value={pricing.cleaningFee || ''}
                onChange={(e) =>
                  onPricingChange({
                    cleaningFee: e.target.value ? parseFloat(e.target.value) : null,
                  })
                }
                placeholder="0"
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">One-time fee per booking</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Security deposit
              <span className="text-gray-400 font-normal ml-2">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                $
              </span>
              <input
                type="number"
                min="0"
                value={pricing.securityDeposit || ''}
                onChange={(e) =>
                  onPricingChange({
                    securityDeposit: e.target.value ? parseFloat(e.target.value) : null,
                  })
                }
                placeholder="0"
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Refundable if no damage occurs
            </p>
          </div>
        </div>
      </div>

      {/* Estimated Earnings */}
      {pricing.nightlyRate > 0 && (
        <div className="bg-primary-50 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900">Estimated monthly earnings</h4>
              <p className="text-sm text-gray-600 mt-1">
                Based on 60% occupancy rate
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-primary-600">
                ${estimatedMonthly.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">per month</p>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Tips */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h4 className="font-medium text-gray-900 mb-2">Pricing tips</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Research similar listings in your area</li>
          <li>• Start lower to attract first reviews</li>
          <li>• Adjust prices seasonally for higher demand</li>
          <li>• Offer weekly/monthly discounts for longer stays</li>
        </ul>
      </div>
    </div>
  );
};

export default PricingStep;
